import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useRegisteredMutation } from './mutation';
import { adminConfig, adminLlmUsage, adminProviderDrift, adminRemediations } from './resources';
import type { ProviderDriftQuery } from './resources';
import type {
  RemediationApplyRequest,
  RemediationApplyResponse,
  RemediationPreview,
  RemediationPreviewRequest,
  ConfigApplyStatus,
  ConfigSecretMetadata,
  ConfigSecretPath,
  ConfigSecretUpdate,
} from './types.gen';

export type {
  ConfigScalar,
  ConfigValue,
  ConfigDocument,
  ConfigWireState,
  ConfigWireNode,
  AdminConfigWireResponse,
  AdminConfigWirePut,
  AdminConfigWirePutResponse,
} from './config-types';
import type { AdminConfigWirePut, AdminConfigWirePutResponse } from './config-types';

export function useAdminConfig() {
  return useQuery(adminConfig());
}

/** The Spend panel's readout of SMART's own Claude calls (Admin → Config → AI/LLM). */
export function useAdminLlmUsage() {
  return useQuery(adminLlmUsage());
}

export function useValidateAdminConfig() {
  return useRegisteredMutation({
    noDataEffect: true,
    gcTime: 0,
    mutationFn: (body: AdminConfigWirePut) =>
      api.post<{ valid: true; secrets: ConfigSecretMetadata[] }>(
        '/admin/config/validate',
        { body },
      ),
  });
}

export function useSaveAdminConfig() {
  const queryClient = useQueryClient();
  return useRegisteredMutation({
    domains: ['configuration'],
    gcTime: 0,
    mutationFn: (body: AdminConfigWirePut) =>
      api.put<AdminConfigWirePutResponse>('/admin/config', { body }),
    // Settled, not just success: a conflict is exactly when the editor most
    // needs the current desired_version in order to show a usable message.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: adminConfig.key() });
    },
  });
}

export function useRetryAdminConfig() {
  const queryClient = useQueryClient();
  return useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: (_: void) => api.post('/admin/config/retry'),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: adminConfig.key() });
    },
  });
}

/// Every managed secret, in the order `ConfigSecretPath::ALL` declares them.
///
/// `admin-config.secret-paths.guard.test.ts` pins this against the generated
/// `ConfigSecretPath` union. An incomplete array is still a valid
/// `ConfigSecretPath[]`, so TypeScript says nothing when an entry is missed —
/// and a missed entry means the credential never renders a control and cannot
/// be entered at all.
export const CONFIG_SECRET_PATHS: ConfigSecretPath[] = [
  'encorepro_postgres.password',
  'meta.access_token',
  'meta.app_secret',
  'ttd.bearer',
  'ttd.crm_bearer',
  'r2.s3_access_key_id',
  'r2.s3_secret',
  'enrich.api_key',
  'email.api_token',
  'alerts.webhook_url',
  'conversions.meta.access_token',
  'conversions.ttd.auth_secret',
  'conversions.uid2.api_key',
  'conversions.uid2.client_secret_b64',
  'llm.api_key',
  'invoicing.xero_client_secret',
  'pixel_kv.api_token',
];


// ── Remediations ──────────────────────────────────────────────────────────
//
// They live beside the config hooks because they share the gate — Full
// `config_manage` plus an unscoped admin — and nothing else in the app may
// call them.

export function useRemediationCandidates() {
  return useQuery(adminRemediations());
}

// ── Provider drift ────────────────────────────────────────────────────────
//
// Same gate again — Full `config_manage` plus an unscoped admin, because drift
// spans every production and the per-entity scope checks that would make a
// scoped read safe do not exist. Read-only: the sweep repairs, this only says
// what it saw.
//
// The query object must come from `providerDriftQuery`, never from a literal —
// see the note on `ProviderDriftQuery`.
export function useProviderDrift(query: ProviderDriftQuery) {
  return useQuery(adminProviderDrift(query));
}

/**
 * The dry run. A POST despite writing nothing: it carries a structured target
 * in its body and hands out the fingerprint `apply` consumes.
 */
export function usePreviewRemediation() {
  return useRegisteredMutation({
    noDataEffect: true,
    gcTime: 0,
    mutationFn: (body: RemediationPreviewRequest) =>
      api.post<RemediationPreview>('/admin/remediations/preview', { body }),
  });
}

/**
 * `retry: false`, and not as a preference.
 *
 * A remediation is a state-changing write whose whole safety argument is that
 * it runs once against a state someone reviewed. An automatic second attempt
 * after a timeout would re-run the preflight against a world the first attempt
 * may already have changed — the same reasoning that removed retries from the
 * planner preview, and the same reasoning the operator scripts state as "a
 * DELETE that appears to have failed may well have committed."
 */
export function useApplyRemediation() {
  const queryClient = useQueryClient();
  return useRegisteredMutation({
    domains: ['configuration', 'campaigns', 'audiences', 'creatives'],
    gcTime: 0,
    retry: false,
    mutationFn: (body: RemediationApplyRequest) =>
      api.post<RemediationApplyResponse>('/admin/remediations/apply', { body }),
    // Settled, not success: a 409 means the entity moved, and the candidate
    // list is exactly what the operator needs re-read to see where it went.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: adminRemediations.key() });
    },
  });
}
